(function () {
  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  window.addEventListener('pageswap', async (event) => {
    const { viewTransition } = /** @type {PageSwapEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          viewTransition.skipTransition();
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      sessionStorage.setItem('custom-transition-type', transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      sessionStorage.removeItem('custom-transition-type');
    }
  });

  window.addEventListener('pagereveal', async (event) => {
    const { viewTransition } = /** @type {PageRevealEvent} */ (event);

    if (shouldSkipViewTransition(viewTransition)) {
      /** @type {ViewTransition | null} */ (viewTransition)?.skipTransition();
      return;
    }

    const customTransitionType = sessionStorage.getItem('custom-transition-type');

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      await viewTransition.finished;

      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');

      idleCallback(() => {
        sessionStorage.removeItem('custom-transition-type');
        document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
          element.removeAttribute('data-view-transition-type');
        });
      });
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  });

  /**
   * @param {ViewTransition | null} viewTransition
   * @returns {viewTransition is null}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !(viewTransition instanceof ViewTransition) || isLowPowerDevice();
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();

(function () {
  if (window.azOfferImageFallbackReady) return;
  window.azOfferImageFallbackReady = true;

  const offerImageNames = [
    'dr-lamp-demo-03.webp',
    'chatgpt-image-12-de-jun-de-2026-21_55_58-9.webp',
    'chatgpt-image-12-de-jun-de-2026-21_55_57-3.webp',
    'chatgpt-image-14-de-jun-de-2026-14_52_34.webp',
    'topviral-luzsolar-content-03.webp',
    'chatgpt-image-12-de-jun-de-2026-21_55_57-4.webp',
    'chatgpt-image-12-de-jun-de-2026-21_55_56-1.webp',
    'chatgpt-image-12-de-jun-de-2026-21_55_58-7.webp',
    'chatgpt-image-12-de-jun-de-2026-21_55_58-8.webp',
  ];

  const selector = offerImageNames
    .map((name) => `img[src*="${name}"], img[srcset*="${name}"], img[data-src*="${name}"], img[data-srcset*="${name}"]`)
    .join(',');

  const resizeUrl = (url, width) => {
    if (!url) return url;

    if (url.includes('width=')) {
      return url.replace(/([?&]width=)\d+/g, `$1${width}`);
    }

    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const loadImage = (image) => {
    if (!image || image.dataset.loaded === 'true') return;

    if (image.dataset.srcset) {
      image.setAttribute('srcset', image.dataset.srcset);
    }

    if (image.dataset.src) {
      image.setAttribute('src', image.dataset.src);
    }

    image.dataset.loaded = 'true';
  };

  const lazyObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              loadImage(entry.target);
              lazyObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '220px 0px' }
        )
      : null;

  const observeImage = (image) => {
    if (lazyObserver) {
      lazyObserver.observe(image);
      return;
    }

    loadImage(image);
  };

  const prepareImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return;
    if (image.dataset.azOfferPrepared === 'true') {
      observeImage(image);
      return;
    }

    const originalSrc = image.getAttribute('src') || image.dataset.src || '';
    const originalSrcset = image.getAttribute('srcset') || image.dataset.srcset || '';
    const isOfferImage = offerImageNames.some((name) => originalSrc.includes(name) || originalSrcset.includes(name));

    if (!isOfferImage) return;

    image.dataset.azOfferPrepared = 'true';
    image.dataset.src = resizeUrl(originalSrc, 1200);

    if (originalSrcset) {
      image.dataset.srcset = originalSrcset;
      image.removeAttribute('srcset');
    }

    image.setAttribute('src', resizeUrl(originalSrc, 24));
    image.setAttribute('loading', 'lazy');
    image.setAttribute('decoding', 'async');
    image.classList.add('az-delayed-image');

    observeImage(image);
  };

  const scan = (root = document) => {
    root.querySelectorAll(selector).forEach(prepareImage);
  };

  scan();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        if (node.matches(selector)) {
          prepareImage(node);
        }

        scan(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
